import { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import { useRegion } from '../auth/RegionContext';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';
import { TransitionButton } from '../actions/TransitionButton';
import { JustifiedAction } from '../actions/JustifiedAction';
import { DsarPackage } from './DsarPackage';
import type { DsarRow as Row } from './dsarQueueModel';
import {
  DEADLINE_PHASE_COLOR,
  DEADLINE_PHASE_LABEL,
  DSAR_STATUS_COLOR,
  DSAR_STATUS_LABEL,
} from './dsarState';

/**
 * One row of the DSAR queue (D-dsar). The request, its status, the derived statutory-deadline
 * chip, the member, the dates, and the lifecycle actions where the compliance capability
 * permits. Fulfilling assembles and displays the member's data package (the DsarPackage view)
 * and records the request fulfilled through the chokepoint; rejecting captures a MANDATORY
 * reason via JustifiedAction. The acting officer is recorded by the audit floor, never stored
 * on the row.
 */
export const DsarRow = ({ row, now }: { row: Row; now: number }) => {
  const { accessibleRegions } = useRegion();
  const { role } = useAccessContext();
  const ctx = { role, accessibleRegions };
  const can = (capability: Parameters<typeof hasAccess>[1]['capability']) =>
    hasAccess(ctx, { capability });
  const [showPackage, setShowPackage] = useState(false);

  const iso = new Date(now).toISOString();
  const isReceived = row.status === 'received';
  const isInProgress = row.status === 'in_progress';
  const isUnresolved = isReceived || isInProgress;

  return (
    <Card
      variant="outlined"
      data-testid={`dsar-request-${row.id}`}
      sx={{ mb: 2 }}
    >
      <CardContent>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
        >
          <Box sx={{ minWidth: 220 }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {row.memberName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.memberEmail} | {row.market.toUpperCase()}
            </Typography>
          </Box>
          <Chip
            size="small"
            variant="outlined"
            color={DSAR_STATUS_COLOR[row.status]}
            label={DSAR_STATUS_LABEL[row.status]}
            data-testid={`dsar-status-${row.id}`}
          />
          {row.deadlinePhase && (
            <Chip
              size="small"
              variant="filled"
              color={DEADLINE_PHASE_COLOR[row.deadlinePhase]}
              label={DEADLINE_PHASE_LABEL[row.deadlinePhase]}
              data-testid={`dsar-deadline-${row.id}`}
            />
          )}
          <Typography variant="caption" color="text.secondary">
            requested {row.requestedAt.slice(0, 10)} | due{' '}
            {row.dueAt.slice(0, 10)}
          </Typography>
        </Stack>

        {row.status === 'rejected' && row.rejectionReason && (
          <Typography
            variant="caption"
            color="text.secondary"
            component="div"
            sx={{ mt: 1 }}
            data-testid={`dsar-rejection-${row.id}`}
          >
            Rejected: {row.rejectionReason}
          </Typography>
        )}

        <Stack
          direction="row"
          spacing={2}
          sx={{ mt: 2 }}
          useFlexGap
          flexWrap="wrap"
        >
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => setShowPackage((v) => !v)}
            data-testid={`dsar-package-toggle-${row.id}`}
          >
            {showPackage ? 'Hide data package' : 'View data package'}
          </Button>

          {isReceived && can('dsar:process') && (
            <TransitionButton
              resource="dsarRequests"
              capability="dsar:process"
              id={row.id}
              fromStatus="received"
              toStatus="in_progress"
              label="Take up"
              confirmTitle="Take up this request"
              confirmContent="Move the request to in progress. The statutory clock keeps running."
              successMessage="Request taken up"
            />
          )}

          {isInProgress && can('dsar:fulfil') && (
            <TransitionButton
              resource="dsarRequests"
              capability="dsar:fulfil"
              id={row.id}
              data={{ status: 'fulfilled', fulfilledAt: iso }}
              previousData={{
                id: row.id,
                status: 'in_progress',
                fulfilledAt: row.fulfilledAt,
              }}
              label="Fulfil"
              color="success"
              confirmTitle="Fulfil this request"
              confirmContent="Record the request fulfilled. The assembled data package is the release; produce it for the member."
              successMessage="Request fulfilled"
            />
          )}

          {isUnresolved && can('dsar:reject') && (
            <JustifiedAction
              resource="dsarRequests"
              capability="dsar:reject"
              id={row.id}
              label="Reject"
              color="error"
              dialogTitle="Reject this request"
              dialogIntro="Refuse the request as invalid or unverified. Refusing a statutory request must be defensible, so a reason is required and is recorded."
              buildData={(justification) => ({
                status: 'rejected',
                rejectedAt: iso,
                rejectionReason: justification,
              })}
              previousData={{
                id: row.id,
                status: row.status,
                rejectedAt: null,
                rejectionReason: null,
              }}
              successMessage="Request rejected"
            />
          )}
        </Stack>

        {showPackage && <DsarPackage subjectUserId={row.subjectUserId} />}
      </CardContent>
    </Card>
  );
};
